import { type FunctionComponent, type JSX, useContext, useMemo } from "react";

import {
  DEFAULT_SUBSTRUCTURE_INSTANCE_BUTTON_ORDER,
  DEFAULT_SUBSTRUCTURE_INSTANCE_MENU_ORDER,
  SubstructureEmbedded,
  SubstructureEmbeddedContent,
  SubstructureInstanceButton,
  SubstructureLayoutProps,
  SubstructureStub,
  SubstructureVariant,
  type SubstructureVariantComponentProps,
  useSubstructureLayout,
} from "@plansoft-configuration-renderer/components";
import { ControlLayoutContext } from "@plansoft-configuration-renderer/layout";

import { CustomSubstructureAddButton } from "./CustomSubstructureAddButton";
import { CustomSubstructureStubAsButton } from "./CustomSubstructureStubAsButton";

type CustomType = {
  renderAsCustomButton?: boolean;
  iconKey?: string;
  awaitFunction?: string;
};

interface CustomSubstructureLayoutProps extends SubstructureLayoutProps {
  custom?: CustomType;
}

const substructureVariants: Record<
  SubstructureVariant,
  FunctionComponent<SubstructureVariantComponentProps>
> = {
  [SubstructureVariant.Embedded]: SubstructureEmbedded,
  [SubstructureVariant.Default]: SubstructureEmbedded,
  [SubstructureVariant.Stub]: SubstructureStub,
  [SubstructureVariant.Content]: SubstructureEmbeddedContent,
};

const HIDDEN_STUB_INSTANCE_BUTTONS: Array<SubstructureInstanceButton> = [
  SubstructureInstanceButton.Edit,
];

function filterInstanceButtons(
  buttons: Array<SubstructureInstanceButton>,
): Array<SubstructureInstanceButton> {
  return buttons.filter((b) => !HIDDEN_STUB_INSTANCE_BUTTONS.includes(b));
}

export function CustomSubstructureMapper(
  props: Partial<CustomSubstructureLayoutProps>,
): JSX.Element {
  const { variant } = useSubstructureLayout();
  const contextValue = useContext(ControlLayoutContext);

  const filteredContextValue = useMemo(() => {
    const { instanceButtonOrder, instanceMenuOrder, ...rest } =
      contextValue as Record<string, unknown>;

    return {
      ...rest,
      instanceButtonOrder: filterInstanceButtons(
        (instanceButtonOrder as Array<SubstructureInstanceButton>) ??
          DEFAULT_SUBSTRUCTURE_INSTANCE_BUTTON_ORDER,
      ),
      instanceMenuOrder: filterInstanceButtons(
        (instanceMenuOrder as Array<SubstructureInstanceButton>) ??
          DEFAULT_SUBSTRUCTURE_INSTANCE_MENU_ORDER,
      ),
    };
  }, [contextValue]);

  if (
    variant === SubstructureVariant.Stub &&
    props.custom?.renderAsCustomButton
  ) {
    return (
      <ControlLayoutContext.Provider value={filteredContextValue}>
        <CustomSubstructureStubAsButton
          iconKey={props.custom?.iconKey}
          awaitFunction={props.custom?.awaitFunction}
        />
        <CustomSubstructureAddButton />
      </ControlLayoutContext.Provider>
    );
  }

  const VariantComponent = substructureVariants[variant];
  return <VariantComponent />;
}

CustomSubstructureMapper.displayName = "CustomSubstructureMapper";
